import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';

export const TOUR_STEPS = [
  {
    target: 'join-code',
    title: 'Your Team Code',
    body: 'This is your Team Code — share it with parents so players can join your roster instantly. Tap to copy it!',
  },
  {
    target: 'add-players',
    title: 'Add Your First Player',
    body: 'Search for players by username and add them to your roster. Once they join with your code they appear here automatically.',
  },
  {
    target: 'add-content',
    title: 'Assign Your First Piece of Content',
    body: 'Assign YouTube videos, articles, or quick tips directly to your players. They get notified instantly on their home screen.',
  },
  {
    target: 'analytics',
    title: 'Track Your Team',
    body: "See who's active, who's falling behind, and how your team is progressing — all in one place.",
  },
] as const;

type Phase = 'welcome' | 'tour' | 'celebrate';

interface Rect { top: number; left: number; width: number; height: number }

function findRect(target: string): Rect | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

interface CoachTourProps {
  open: boolean;
  /** Called before the tour starts so the dashboard can open a team detail view. */
  onStart?: () => void;
  onSkip: () => void;
  onComplete: () => void;
  /** Fired by the celebration CTA. */
  onAssignContent: () => void;
  /** Coach already assigned something — skip the "first lesson" prompt. */
  hasAssignments?: boolean;
}

export default function CoachTour({ open, onStart, onSkip, onComplete, onAssignContent, hasAssignments = false }: CoachTourProps) {
  const [phase, setPhase] = useState<Phase>('welcome');
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (open) { setPhase('welcome'); setStep(0); setRect(null); }
  }, [open]);

  const measure = useCallback(() => {
    if (phase !== 'tour') return;
    setRect(findRect(TOUR_STEPS[step].target));
  }, [phase, step]);

  // Scroll the target into view, then poll briefly until it is measurable.
  useEffect(() => {
    if (!open || phase !== 'tour') return;
    let frames = 0;
    const el = document.querySelector<HTMLElement>(`[data-tour="${TOUR_STEPS[step].target}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const id = window.setInterval(() => {
      frames += 1;
      setRect(findRect(TOUR_STEPS[step].target));
      if (frames > 30) window.clearInterval(id);
    }, 60);
    return () => window.clearInterval(id);
  }, [open, phase, step]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  if (!open) return null;

  const handleStart = () => {
    onStart?.();
    setPhase('tour');
    setStep(0);
  };

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) setStep(s => s + 1);
    else setPhase('celebrate');
  };

  const finish = () => { onComplete(); };

  // Tooltip placement: below the target when there's room, otherwise above.
  const pad = 10;
  const tooltipTop = rect
    ? rect.top + rect.height + pad + 220 > window.innerHeight && rect.top > 240
      ? Math.max(12, rect.top - pad - 210)
      : Math.min(rect.top + rect.height + pad, window.innerHeight - 230)
    : 0;

  return createPortal(
    <AnimatePresence>
      {phase === 'welcome' && (
        <motion.div
          key="welcome"
          className="fixed inset-0 z-[1200] bg-background/90 backdrop-blur-md flex items-center justify-center p-5"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          role="dialog" aria-modal="true" aria-label="Welcome to Coaches Hub"
        >
          <motion.div
            initial={{ scale: 0.94, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            className="w-full max-w-sm rounded-2xl glass-card ring-1 ring-primary/40 shadow-elevated p-6 text-center"
          >
            <div className="flex justify-center mb-4"><Logo className="w-14 h-14" /></div>
            <h2 className="font-display font-extrabold text-2xl text-foreground">
              Welcome to Coaches Hub!
            </h2>
            <p className="text-sm text-muted-foreground font-body mt-2">
              Let's get your team set up in 4 quick steps
            </p>
            <Button onClick={handleStart} className="w-full mt-6 font-display font-bold h-11">
              <Sparkles className="w-4 h-4 mr-2" /> Let's Go!
            </Button>
            <button
              onClick={onSkip}
              className="mt-3 text-xs font-body text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip for now
            </button>
          </motion.div>
        </motion.div>
      )}

      {phase === 'tour' && (
        <motion.div
          key="tour"
          className="fixed inset-0 z-[1200]"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          {/* Spotlight — dark everywhere except the highlighted element */}
          {rect ? (
            <motion.div
              className="absolute rounded-xl ring-2 ring-primary pointer-events-none"
              animate={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{ boxShadow: '0 0 0 9999px hsl(var(--background) / 0.82)' }}
            />
          ) : (
            <div className="absolute inset-0 bg-background/82" />
          )}

          {/* Tooltip */}
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 w-[min(22rem,calc(100vw-2rem))] rounded-2xl glass-card border-2 border-[#2563eb] shadow-elevated p-4"
            style={rect ? { top: tooltipTop } : { top: '50%' }}
          >
            <p className="text-[10px] font-display font-bold uppercase tracking-wider text-primary">
              Step {step + 1} of {TOUR_STEPS.length}
            </p>
            <h3 className="font-display font-bold text-base text-foreground mt-1">
              {TOUR_STEPS[step].title}
            </h3>
            <p className="text-xs text-muted-foreground font-body mt-1.5 leading-relaxed">
              {TOUR_STEPS[step].body}
            </p>
            <div className="flex items-center justify-between gap-3 mt-4">
              <div className="flex gap-1.5">
                {TOUR_STEPS.map((s, i) => (
                  <span
                    key={s.target}
                    className={`h-1.5 rounded-full transition-all ${i === step ? 'w-5 bg-primary' : 'w-1.5 bg-white/20'}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onSkip} className="text-[11px] font-body text-muted-foreground hover:text-foreground">
                  Skip
                </button>
                <Button size="sm" onClick={handleNext} className="font-display font-bold h-8">
                  {step === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {phase === 'celebrate' && (
        <motion.div
          key="celebrate"
          className="fixed inset-0 z-[1200] bg-background/90 backdrop-blur-md flex items-center justify-center p-5 overflow-hidden"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          {Array.from({ length: 28 }).map((_, i) => (
            <motion.span
              key={i}
              className="absolute top-0 rounded-sm"
              style={{
                left: `${(i * 37) % 100}%`,
                width: 7,
                height: 11,
                backgroundColor: ['hsl(210 100% 50%)', 'hsl(35 95% 55%)', 'hsl(160 60% 45%)', 'hsl(280 80% 60%)'][i % 4],
              }}
              initial={{ y: -30, opacity: 1, rotate: 0 }}
              animate={{ y: window.innerHeight + 40, rotate: 540, opacity: [1, 1, 0] }}
              transition={{ duration: 1.8 + (i % 5) * 0.2, delay: (i % 7) * 0.08, ease: 'easeIn' }}
            />
          ))}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 250, damping: 20 }}
            className="relative w-full max-w-sm rounded-2xl glass-card ring-1 ring-primary/40 shadow-elevated p-6 text-center"
          >
            <div className="w-14 h-14 rounded-2xl bg-primary/15 ring-1 ring-primary/40 flex items-center justify-center mx-auto mb-3">
              <Trophy className="w-7 h-7 text-primary" />
            </div>
            <h2 className="font-display font-extrabold text-2xl text-foreground">You're all set Coach! 🏆</h2>
            <Button
              onClick={() => { finish(); if (!hasAssignments) onAssignContent(); }}
              className="w-full mt-5 font-display font-bold h-11"
            >
              {hasAssignments ? 'Go to your team dashboard' : 'Your team is waiting — assign your first lesson!'}
            </Button>
            <button
              onClick={finish}
              className="mt-3 text-xs font-body text-muted-foreground hover:text-foreground transition-colors"
            >
              I'll do it later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
